import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BotProfile } from "./types";
import LoadingSpinner from "@/components/LoadingSpinner";
import useSite from "@/hooks/useSite";
import useSubscriptions from "@/hooks/useSubscriptions";

interface Props {
  botProfile?: BotProfile;
  onClose(): void;
  onSave(profile: BotProfile): void;
}

const DEFAULT_PICTURE =
  "https://velaromessagingstaging.blob.core.windows.net/images/default-bot-pfp.png";

// ─── Avatar presets ───────────────────────────────────────────────────────────

const AVATAR_CATEGORIES = [
  {
    label: "🤖 AI Bots",
    avatars: [
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Velaro&backgroundColor=b6e3f4", label: "Velaro" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Atlas&backgroundColor=c0aede", label: "Atlas" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Echo&backgroundColor=d1d4f9", label: "Echo" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Nexus&backgroundColor=ffd5dc", label: "Nexus" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Pixel&backgroundColor=ffdfbf", label: "Pixel" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Orion&backgroundColor=b6e3f4", label: "Orion" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Nova&backgroundColor=c0aede", label: "Nova" },
      { url: "https://api.dicebear.com/8.x/bottts/svg?seed=Cipher&backgroundColor=d1d4f9", label: "Cipher" }
    ]
  },
  {
    label: "👤 Personas",
    avatars: [
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Alex&backgroundColor=b6e3f4", label: "Alex" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Morgan&backgroundColor=c0aede", label: "Morgan" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Riley&backgroundColor=d1d4f9", label: "Riley" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Jamie&backgroundColor=ffd5dc", label: "Jamie" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Taylor&backgroundColor=ffdfbf", label: "Taylor" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Jordan&backgroundColor=b6e3f4", label: "Jordan" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Casey&backgroundColor=c0aede", label: "Casey" },
      { url: "https://api.dicebear.com/8.x/notionists/svg?seed=Quinn&backgroundColor=d1d4f9", label: "Quinn" }
    ]
  },
  {
    label: "✨ Illustrated",
    avatars: [
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Aria&backgroundColor=b6e3f4", label: "Aria" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Blaze&backgroundColor=ffd5dc", label: "Blaze" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Cove&backgroundColor=d1d4f9", label: "Cove" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Flux&backgroundColor=c0aede", label: "Flux" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Ember&backgroundColor=ffdfbf", label: "Ember" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Sage&backgroundColor=b6e3f4", label: "Sage" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Dusk&backgroundColor=ffd5dc", label: "Dusk" },
      { url: "https://api.dicebear.com/8.x/micah/svg?seed=Zara&backgroundColor=c0aede", label: "Zara" }
    ]
  }
];

// ─── LLM model options ────────────────────────────────────────────────────────

const LLM_MODELS = [
  { value: "",                            label: "Velaro Default",          provider: "Velaro",    tier: "All plans",    desc: "Uses your site's assigned model" },
  { value: "velaro-gpt-4o-mini",          label: "GPT-4o Mini",             provider: "OpenAI",    tier: "Basic",        desc: "Fast, cost-efficient" },
  { value: "velaro-gpt-4o",              label: "GPT-4o",                  provider: "OpenAI",    tier: "Pro",          desc: "Best OpenAI model" },
  { value: "velaro-gpt-5",               label: "GPT-5",                   provider: "OpenAI",    tier: "Enterprise",   desc: "Most capable OpenAI" },
  { value: "velaro-gpt-5-mini",          label: "GPT-5 Mini",              provider: "OpenAI",    tier: "Enterprise",   desc: "Fast GPT-5" },
  { value: "claude-3-haiku-20240307",     label: "Claude 3 Haiku",          provider: "Anthropic", tier: "Pro",          desc: "Fast, lightweight" },
  { value: "claude-3-5-haiku-20241022",   label: "Claude 3.5 Haiku",        provider: "Anthropic", tier: "Pro",          desc: "Improved Haiku" },
  { value: "claude-3-5-sonnet-20240620",  label: "Claude 3.5 Sonnet",       provider: "Anthropic", tier: "Enterprise",   desc: "Best Claude balance" },
  { value: "gemini-2.0-flash",            label: "Gemini 2.0 Flash",        provider: "Google",    tier: "Pro",          desc: "Fast multimodal" },
  { value: "gemini-1.5-pro",             label: "Gemini 1.5 Pro",          provider: "Google",    tier: "Enterprise",   desc: "Advanced reasoning" },
];

const PROVIDER_COLORS: Record<string, string> = {
  Velaro:    "bg-grape-100 text-grape-700",
  OpenAI:    "bg-success-green-100 text-success-green-700",
  Anthropic: "bg-sweet-orange-100 text-sweet-orange-700",
  Google:    "bg-cornflower-blue-100 text-cornflower-blue-700",
};

// ─── Tone options ────────────────────────────────────────────────────────────

const TONES = [
  { value: "friendly",     label: "Friendly",      emoji: "😊", desc: "Warm, approachable, conversational" },
  { value: "professional", label: "Professional",  emoji: "💼", desc: "Formal, precise, business-appropriate" },
  { value: "empathetic",   label: "Empathetic",    emoji: "💙", desc: "Caring, patient, emotionally aware" },
  { value: "concise",      label: "Concise",       emoji: "⚡", desc: "Short answers, gets to the point fast" },
];

// ─── Component ───────────────────────────────────────────────────────────────

type AvatarTab = "presets" | "upload";
type EditSection = "identity" | "character" | "voice" | "model";

export default function EditBotProfileModal({ botProfile, onClose, onSave }: Props) {
  const { site } = useSite();
  const api = useApi();
  const { subscription } = useSubscriptions();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Identity
  const [name, setName] = useState(botProfile?.name || site.companyName + " Bot");
  const [avatarTab, setAvatarTab] = useState<AvatarTab>("presets");
  const [selectedPresetUrl, setSelectedPresetUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isDefault, setIsDefault] = useState(botProfile?.isDefault ?? false);

  // Character
  const [tone, setTone] = useState(botProfile?.tone || "friendly");
  const [greeting, setGreeting] = useState(botProfile?.greeting || "");

  // Voice
  const [enableVoice, setEnableVoice] = useState(botProfile?.enableVoice ?? false);
  const [voiceId, setVoiceId] = useState(botProfile?.voiceId || "");
  const [voiceModel, setVoiceModel] = useState(botProfile?.voiceModel || "eleven_turbo_v2_5");
  const [voiceAutoPlay, setVoiceAutoPlay] = useState(botProfile?.voiceAutoPlay ?? false);
  const [voices, setVoices] = useState<{ voiceId: string; name: string; category: string }[]>([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voiceUsage, setVoiceUsage] = useState<{
    charsUsed: number; charsMax: number; percent: number;
    estimatedCostUsd: number; warningLevel: string; isUnlimited: boolean; monthLabel: string;
  } | null>(null);

  // Model
  const [aiModel, setAiModel] = useState(botProfile?.aiModel || "");

  // UI state
  const [activeSection, setActiveSection] = useState<EditSection>("identity");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNew = !botProfile;

  // Load ElevenLabs voices when voice section is opened
  useEffect(() => {
    if (activeSection !== "voice" || !enableVoice || voices.length > 0) return;
    setVoicesLoading(true);
    api.messaging.get("AIConfiguration/voices")
      .then((r) => r.json())
      .then((data) => setVoices(Array.isArray(data) ? data : []))
      .catch(() => setVoices([]))
      .finally(() => setVoicesLoading(false));
  }, [activeSection, enableVoice, voices.length, api.messaging]);

  // Load voice quota usage when voice section is opened
  useEffect(() => {
    if (activeSection !== "voice" || voiceUsage) return;
    api.admin.get("ElevenLabs/Usage")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setVoiceUsage(data); })
      .catch(() => {}); // usage meter is non-critical, fail silently
  }, [activeSection, voiceUsage, api.admin]);

  const previewUrl = useMemo(() => {
    if (avatarTab === "upload" && uploadedFile) return URL.createObjectURL(uploadedFile);
    if (avatarTab === "presets" && selectedPresetUrl) return selectedPresetUrl;
    return botProfile?.picture || DEFAULT_PICTURE;
  }, [avatarTab, uploadedFile, selectedPresetUrl, botProfile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) setUploadedFile(file);
  }, []);

  async function save() {
    setSending(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("Id", (botProfile?.id || 0).toString());
      formData.append("name", name);
      formData.append("tone", tone);
      formData.append("greeting", greeting);
      formData.append("aiModel", aiModel);
      formData.append("isDefault", isDefault.toString());
      formData.append("enableVoice", enableVoice.toString());
      formData.append("voiceId", voiceId);
      formData.append("voiceModel", voiceModel);
      formData.append("voiceAutoPlay", voiceAutoPlay.toString());

      if (avatarTab === "upload" && uploadedFile) {
        formData.append("picture", uploadedFile);
      } else if (avatarTab === "presets" && selectedPresetUrl) {
        formData.append("pictureUrl", selectedPresetUrl);
      }

      const resp = await api.messaging.postFormData("BotProfiles", formData);
      if (resp.ok) {
        onSave(await resp.json());
      } else {
        setError("Failed to save. Please try again.");
      }
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSending(false);
    }
  }

  const sections: { id: EditSection; label: string; icon: string }[] = [
    { id: "identity",  label: "Identity",  icon: "fa-user-circle" },
    { id: "character", label: "Character", icon: "fa-magic" },
    { id: "voice",     label: "Voice",     icon: "fa-microphone" },
    { id: "model",     label: "AI Model",  icon: "fa-cpu" },
  ];

  const selectedModel = LLM_MODELS.find((m) => m.value === aiModel) || LLM_MODELS[0];

  return (
    <Modal maxWidth="max-w-4xl" show={true}>
      <form onSubmit={(e) => { e.preventDefault(); save(); }}>
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-gray-200">
          <div>
            <h2 className="text-lg font-bold text-slate-gray-800">
              {isNew ? "Create Bot Persona" : "Edit Bot Persona"}
            </h2>
            <p className="text-xs text-slate-gray-500 mt-0.5">
              Identity · Character · Voice · AI Model
            </p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-gray-100 text-slate-gray-400 hover:text-slate-gray-700 transition-colors"
            aria-label="Close">
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="flex" style={{ minHeight: "480px" }}>
          {/* ── Left nav ── */}
          <div className="w-40 flex-shrink-0 border-r border-slate-gray-200 bg-slate-gray-50 flex flex-col">
            {/* Avatar preview */}
            <div className="p-4 flex flex-col items-center border-b border-slate-gray-200">
              <div className="relative">
                <img src={previewUrl} alt="Preview"
                  className="w-16 h-16 rounded-full border-4 border-white shadow-md object-cover" />
                {isDefault && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-sweet-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                    <i className="fa fa-star text-white" style={{ fontSize: "8px" }} />
                  </span>
                )}
              </div>
              <p className="text-xs font-bold text-slate-gray-700 mt-2 text-center truncate w-full">{name || "Bot Name"}</p>
              {tone && (
                <span className="mt-1 text-xs text-slate-gray-400">
                  {TONES.find((t) => t.value === tone)?.emoji} {TONES.find((t) => t.value === tone)?.label}
                </span>
              )}
              {aiModel && (
                <span className={`mt-1.5 px-1.5 py-0.5 rounded text-xs font-medium ${PROVIDER_COLORS[selectedModel.provider]}`}>
                  {selectedModel.label}
                </span>
              )}
            </div>

            {/* Section nav */}
            <nav className="flex-1 p-2 space-y-1">
              {sections.map((s) => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors text-left ${
                    activeSection === s.id
                      ? "bg-cornflower-blue-50 text-cornflower-blue-700 font-semibold"
                      : "text-slate-gray-600 hover:bg-slate-gray-100"
                  }`}>
                  <i className={`fa ${s.icon} w-4 text-center ${activeSection === s.id ? "text-cornflower-blue-500" : "text-slate-gray-400"}`} />
                  {s.label}
                </button>
              ))}
            </nav>

            {/* Mini chat preview */}
            <div className="p-3 border-t border-slate-gray-200">
              <p className="text-xs text-slate-gray-400 mb-2 text-center">Chat preview</p>
              <div className="space-y-1.5">
                <div className="flex items-end gap-1">
                  <img src={previewUrl} alt="" className="w-5 h-5 rounded-full flex-shrink-0 object-cover" />
                  <div className="bg-white rounded-xl rounded-bl-sm px-2 py-1 shadow-sm border border-slate-gray-200 text-xs text-slate-gray-600 max-w-full">
                    {greeting || "Hi! How can I help?"}
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-cornflower-blue-500 rounded-xl rounded-br-sm px-2 py-1 text-xs text-white">
                    I need help…
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right content ── */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            <div className="p-6">
              {/* ── IDENTITY ── */}
              {activeSection === "identity" && (
                <div className="space-y-5">
                  <SectionHeader icon="fa-user-circle" title="Identity" subtitle="Name, avatar, and default settings" />

                  <div>
                    <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-1.5">Name</label>
                    <input type="text" className="input w-full text-sm" value={name} maxLength={50}
                      placeholder="e.g. Sales Bot, Support Assistant"
                      onChange={(e) => setName(e.currentTarget.value)} autoFocus />
                    <p className="text-xs text-slate-gray-400 mt-1">{name.length}/50</p>
                  </div>

                  {/* Default toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-gray-200 bg-slate-gray-50">
                    <div>
                      <p className="text-sm font-semibold text-slate-gray-700">Default Persona</p>
                      <p className="text-xs text-slate-gray-500">Used when no persona is assigned to a workflow</p>
                    </div>
                    <button type="button" onClick={() => setIsDefault((v) => !v)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${isDefault ? "bg-cornflower-blue-500" : "bg-slate-gray-300"}`}
                      role="switch" aria-checked={isDefault}>
                      <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isDefault ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  {/* Avatar picker */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-2">Avatar</label>
                    <div className="flex gap-1 border-b border-slate-gray-200 mb-4">
                      {[{ id: "presets" as AvatarTab, label: "Choose Preset" }, { id: "upload" as AvatarTab, label: "Upload Image" }].map((tab) => (
                        <button key={tab.id} type="button" onClick={() => setAvatarTab(tab.id)}
                          className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${
                            avatarTab === tab.id ? "border-cornflower-blue-500 text-cornflower-blue-600" : "border-transparent text-slate-gray-500 hover:text-slate-gray-700"
                          }`}>
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {avatarTab === "presets" && (
                      <div className="max-h-52 overflow-y-auto pr-1 space-y-4">
                        {AVATAR_CATEGORIES.map((cat) => (
                          <div key={cat.label}>
                            <p className="text-xs font-semibold text-slate-gray-500 mb-2">{cat.label}</p>
                            <div className="grid grid-cols-8 gap-2">
                              {cat.avatars.map((av) => {
                                const selected = selectedPresetUrl === av.url;
                                return (
                                  <button key={av.url} type="button" title={av.label}
                                    onClick={() => setSelectedPresetUrl(av.url)}
                                    className={`relative rounded-full overflow-hidden w-10 h-10 border-2 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400 ${
                                      selected ? "border-cornflower-blue-500 ring-2 ring-cornflower-blue-300 scale-110" : "border-transparent hover:border-slate-gray-300"
                                    }`}>
                                    <img src={av.url} alt={av.label} className="w-full h-full object-cover" loading="lazy" />
                                    {selected && (
                                      <span className="absolute inset-0 flex items-center justify-center bg-cornflower-blue-500/20">
                                        <i className="fa fa-check text-cornflower-blue-600 text-xs" />
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {avatarTab === "upload" && (
                      <div
                        onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                        onDragLeave={() => setIsDraggingOver(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex flex-col items-center justify-center h-36 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                          isDraggingOver ? "border-cornflower-blue-400 bg-cornflower-blue-50"
                            : uploadedFile ? "border-success-green-400 bg-success-green-50"
                            : "border-slate-gray-300 bg-slate-gray-50 hover:border-cornflower-blue-300 hover:bg-cornflower-blue-50"
                        }`}>
                        <input ref={fileInputRef} type="file" accept="image/*" className="sr-only"
                          onChange={(e) => { const f = e.currentTarget.files?.[0]; if (f) setUploadedFile(f); }} />
                        {uploadedFile ? (
                          <><i className="fa fa-check-circle text-success-green-500 text-xl mb-1" />
                            <p className="text-sm font-medium text-success-green-700">{uploadedFile.name}</p>
                            <p className="text-xs text-slate-gray-400">Click to change</p></>
                        ) : (
                          <><i className="fa fa-cloud-upload text-slate-gray-400 text-xl mb-1" />
                            <p className="text-sm font-medium text-slate-gray-600">
                              {isDraggingOver ? "Drop to upload" : "Drag & drop or click"}
                            </p>
                            <p className="text-xs text-slate-gray-400">PNG, JPG, GIF, WebP up to 5MB</p></>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── CHARACTER ── */}
              {activeSection === "character" && (
                <div className="space-y-5">
                  <SectionHeader icon="fa-magic" title="Character"
                    subtitle="Tone is injected into the system prompt. Greeting overrides the first message in any workflow using this persona." />

                  {/* How tone works */}
                  <div className="rounded-lg bg-slate-gray-50 border border-slate-gray-200 p-3">
                    <p className="text-xs font-semibold text-slate-gray-600 mb-2">How tone works at runtime:</p>
                    <div className="space-y-1.5">
                      {[
                        { step: "1", label: "Tone is injected automatically", desc: "You don't need to write tone instructions in the system prompt — Velaro does it for you." },
                        { step: "2", label: "Persona tone wins over node tone", desc: "If the workflow's AI node also has a tone setting, this persona's tone overrides it." },
                        { step: "3", label: "Changes apply instantly", desc: "Update this and save — the next conversation uses the new tone without touching any workflow." },
                      ].map((r) => (
                        <div key={r.step} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-cornflower-blue-100 text-cornflower-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{r.step}</span>
                          <div>
                            <span className="text-xs font-semibold text-slate-gray-700">{r.label}</span>
                            <span className="text-xs text-slate-gray-500"> — {r.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-2">Tone</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TONES.map((t) => (
                        <button key={t.value} type="button" onClick={() => setTone(t.value)}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                            tone === t.value ? "border-cornflower-blue-400 bg-cornflower-blue-50" : "border-slate-gray-200 hover:border-slate-gray-300"
                          }`}>
                          <span className="text-xl leading-none mt-0.5">{t.emoji}</span>
                          <div>
                            <p className={`text-sm font-semibold ${tone === t.value ? "text-cornflower-blue-700" : "text-slate-gray-700"}`}>{t.label}</p>
                            <p className="text-xs text-slate-gray-500 mt-0.5">{t.desc}</p>
                          </div>
                          {tone === t.value && (
                            <i className="fa fa-check-circle text-cornflower-blue-500 ml-auto mt-0.5 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-1.5">
                      Opening Greeting <span className="font-normal text-slate-gray-400 normal-case">(optional)</span>
                    </label>
                    <textarea
                      className="input w-full text-sm resize-none"
                      rows={3}
                      value={greeting}
                      maxLength={300}
                      placeholder={`Hi! I'm ${name || "your assistant"}. How can I help you today?`}
                      onChange={(e) => setGreeting(e.currentTarget.value)}
                    />
                    <p className="text-xs text-slate-gray-400 mt-1">
                      {greeting.length}/300 · Overrides the first bot message in every workflow using this persona.
                      Leave blank to use the workflow's own greeting.
                    </p>
                  </div>
                </div>
              )}

              {/* ── VOICE ── */}
              {activeSection === "voice" && (
                <div className="space-y-5">
                  <SectionHeader icon="fa-microphone" title="Voice (ElevenLabs)"
                    subtitle="Voice is part of the character — when a conversation switches personas (e.g. on transfer), the new persona's voice is used automatically." />

                  {/* How voice works */}
                  <div className="rounded-lg bg-slate-gray-50 border border-slate-gray-200 p-3">
                    <p className="text-xs font-semibold text-slate-gray-600 mb-2">How voice works at runtime:</p>
                    <div className="space-y-1.5">
                      {[
                        { step: "1", label: "Voice is persona-scoped", desc: "Each persona can have its own voice. When a conversation transfers to a new persona, the new voice activates automatically." },
                        { step: "2", label: "ElevenLabs TTS", desc: "Bot replies are converted to speech using ElevenLabs. Requires the Voice feature on your plan." },
                        { step: "3", label: "Auto-play note", desc: "Browsers require a user gesture (click/tap) before playing audio. Auto-play works best after the visitor has interacted with the chat." },
                      ].map((r) => (
                        <div key={r.step} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-cornflower-blue-100 text-cornflower-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{r.step}</span>
                          <div>
                            <span className="text-xs font-semibold text-slate-gray-700">{r.label}</span>
                            <span className="text-xs text-slate-gray-500"> — {r.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Voice usage meter */}
                  {voiceUsage && !voiceUsage.isUnlimited && (
                    <VoiceUsageMeter usage={voiceUsage} />
                  )}

                  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-gray-200 bg-slate-gray-50">
                    <div>
                      <p className="text-sm font-semibold text-slate-gray-700">Enable Voice</p>
                      <p className="text-xs text-slate-gray-500">Bot replies are spoken using ElevenLabs TTS</p>
                    </div>
                    <button type="button" onClick={() => setEnableVoice((v) => !v)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${enableVoice ? "bg-cornflower-blue-500" : "bg-slate-gray-300"}`}
                      role="switch" aria-checked={enableVoice}>
                      <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${enableVoice ? "translate-x-5" : ""}`} />
                    </button>
                  </div>

                  {enableVoice && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-1.5">Voice</label>
                        {voicesLoading ? (
                          <div className="flex items-center gap-2 text-slate-gray-400 text-sm py-3">
                            <i className="fa fa-circle-o-notch fa-spin" /> Loading voices…
                          </div>
                        ) : (
                          <select className="input w-full text-sm" value={voiceId}
                            onChange={(e) => setVoiceId(e.currentTarget.value)}>
                            <option value="">— Select a voice —</option>
                            {voices.map((v) => (
                              <option key={v.voiceId} value={v.voiceId}>{v.name} ({v.category})</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-gray-600 uppercase tracking-wide mb-1.5">Voice Model</label>
                        <select className="input w-full text-sm" value={voiceModel}
                          onChange={(e) => setVoiceModel(e.currentTarget.value)}>
                          <option value="eleven_turbo_v2_5">Turbo v2.5 — fast, low latency (recommended)</option>
                          <option value="eleven_flash_v2_5">Flash v2.5 — fastest, lowest cost</option>
                          <option value="eleven_multilingual_v2">Multilingual v2 — best quality</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-gray-200 bg-slate-gray-50">
                        <div>
                          <p className="text-sm font-semibold text-slate-gray-700">Auto-play</p>
                          <p className="text-xs text-slate-gray-500">Automatically play voice on message arrival</p>
                        </div>
                        <button type="button" onClick={() => setVoiceAutoPlay((v) => !v)}
                          className={`relative w-11 h-6 rounded-full transition-colors ${voiceAutoPlay ? "bg-cornflower-blue-500" : "bg-slate-gray-300"}`}
                          role="switch" aria-checked={voiceAutoPlay}>
                          <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${voiceAutoPlay ? "translate-x-5" : ""}`} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── MODEL ── */}
              {activeSection === "model" && (
                <div className="space-y-5">
                  <SectionHeader icon="fa-cpu" title="AI Model"
                    subtitle="Which LLM this persona uses. The model is resolved in order: (1) this setting if you pick one, (2) your site's default model set by Velaro, (3) the AI node's own model. Pick 'Velaro Default' to inherit automatically." />

                  {/* Inheritance chain explainer */}
                  <div className="rounded-lg bg-slate-gray-50 border border-slate-gray-200 p-3">
                    <p className="text-xs font-semibold text-slate-gray-600 mb-2">How the model is chosen at runtime:</p>
                    <div className="space-y-1.5">
                      {[
                        { step: "1", label: "This persona's model", desc: "If you pick anything below, it always wins." },
                        { step: "2", label: "Site default (from Velaro)", desc: "Set by Velaro per your plan. Superadmin can upgrade." },
                        { step: "3", label: "AI node's model", desc: "The fallback set on the bot/AI node in your workflow." },
                      ].map((r) => (
                        <div key={r.step} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-cornflower-blue-100 text-cornflower-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{r.step}</span>
                          <div>
                            <span className="text-xs font-semibold text-slate-gray-700">{r.label}</span>
                            <span className="text-xs text-slate-gray-500"> — {r.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(() => {
                    const tier = subscription?.aiModelTier ?? "basic";
                    if (tier === "basic") return (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-sweet-orange-50 border border-sweet-orange-200 text-xs text-sweet-orange-800">
                        <i className="fa fa-lock mt-0.5 flex-shrink-0" />
                        <span>You're on the Basic plan — only GPT-4o Mini is available. <a href="mailto:support@velaro.com" className="underline font-semibold">Upgrade to Pro</a> to unlock GPT-4o, Claude, and Gemini.</span>
                      </div>
                    );
                    if (tier === "pro") return (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-cornflower-blue-50 border border-cornflower-blue-200 text-xs text-cornflower-blue-800">
                        <i className="fa fa-info-circle mt-0.5 flex-shrink-0" />
                        <span>Pro plan: GPT-4o, Claude Haiku, and Gemini Flash are available. <a href="mailto:support@velaro.com" className="underline font-semibold">Upgrade to Enterprise</a> for GPT-5 and Claude Sonnet.</span>
                      </div>
                    );
                    return null; // enterprise: no banner needed
                  })()}

                  <div className="space-y-2">
                    {LLM_MODELS.map((m) => {
                      const tier = subscription?.aiModelTier ?? "basic";
                      const locked =
                        (tier === "basic" && (m.tier === "Pro" || m.tier === "Enterprise")) ||
                        (tier === "pro"   && m.tier === "Enterprise");
                      const selected = aiModel === m.value;
                      return (
                        <button key={m.value} type="button"
                          onClick={() => { if (!locked) setAiModel(m.value); }}
                          disabled={locked}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                            locked
                              ? "border-slate-gray-100 bg-slate-gray-50 opacity-60 cursor-not-allowed"
                              : selected
                              ? "border-cornflower-blue-400 bg-cornflower-blue-50"
                              : "border-slate-gray-200 hover:border-slate-gray-300"
                          }`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${selected && !locked ? "text-cornflower-blue-700" : "text-slate-gray-800"}`}>
                                {m.label}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${PROVIDER_COLORS[m.provider]}`}>
                                {m.provider}
                              </span>
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-gray-100 text-slate-gray-500">
                                {m.tier}
                              </span>
                            </div>
                            <p className="text-xs text-slate-gray-500 mt-0.5">{m.desc}</p>
                          </div>
                          {locked
                            ? <i className="fa fa-lock text-slate-gray-400 flex-shrink-0" />
                            : selected && <i className="fa fa-check-circle text-cornflower-blue-500 flex-shrink-0" />
                          }
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-gray-200 bg-slate-gray-50">
          {error ? (
            <p className="text-xs text-crimson-red-600 flex items-center gap-1">
              <i className="fa fa-exclamation-circle" /> {error}
            </p>
          ) : <span />}
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-gray-600 hover:text-slate-gray-800 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={sending || !name.trim()}
              className="px-5 py-2 text-sm font-semibold text-white bg-cornflower-blue-500 hover:bg-cornflower-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {sending ? <><LoadingSpinner /> Saving…</> : <><i className="fa fa-check" /> {isNew ? "Create Persona" : "Save Changes"}</>}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ─── Voice usage meter ────────────────────────────────────────────────────────

interface VoiceUsageData {
  charsUsed: number; charsMax: number; percent: number;
  estimatedCostUsd: number; warningLevel: string; isUnlimited: boolean; monthLabel: string;
}

function VoiceUsageMeter({ usage }: { usage: VoiceUsageData }) {
  const barColor =
    usage.warningLevel === "exceeded" ? "bg-crimson-red-500"
    : usage.warningLevel === "critical" ? "bg-sweet-orange-500"
    : usage.warningLevel === "warning" ? "bg-sweet-orange-400"
    : "bg-success-green-500";

  const labelColor =
    usage.warningLevel === "exceeded" ? "text-crimson-red-700"
    : usage.warningLevel === "critical" ? "text-sweet-orange-700"
    : usage.warningLevel === "warning" ? "text-sweet-orange-600"
    : "text-slate-gray-600";

  const bgColor =
    usage.warningLevel === "exceeded" ? "bg-crimson-red-50 border-crimson-red-200"
    : usage.warningLevel === "critical" ? "bg-sweet-orange-50 border-sweet-orange-200"
    : usage.warningLevel === "warning" ? "bg-sweet-orange-50 border-sweet-orange-100"
    : "bg-slate-gray-50 border-slate-gray-200";

  const formatChars = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K`
    : n.toString();

  return (
    <div className={`rounded-lg border p-3 ${bgColor}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-xs font-semibold ${labelColor}`}>
          {usage.warningLevel === "exceeded"
            ? "⛔ Monthly voice limit reached"
            : usage.warningLevel === "critical"
            ? "🔴 Voice limit almost reached"
            : usage.warningLevel === "warning"
            ? "🟡 Approaching voice limit"
            : "🎙 Voice usage this month"}
        </p>
        <span className="text-xs text-slate-gray-400">{usage.monthLabel}</span>
      </div>

      {/* Progress bar */}
      <div className="h-2 rounded-full bg-slate-gray-200 overflow-hidden mb-2">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${Math.min(usage.percent, 100)}%` }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-gray-600">
          {formatChars(usage.charsUsed)} / {formatChars(usage.charsMax)} chars ({usage.percent}%)
        </span>
        <span className="text-xs text-slate-gray-400">
          ~${usage.estimatedCostUsd.toFixed(3)} cost
        </span>
      </div>

      {usage.warningLevel === "exceeded" && (
        <p className="text-xs text-crimson-red-700 mt-2 font-medium">
          Voice is paused until your limit resets or you upgrade. Contact <a href="mailto:support@velaro.com" className="underline">support@velaro.com</a> to increase your limit.
        </p>
      )}
      {usage.warningLevel === "critical" && (
        <p className="text-xs text-sweet-orange-700 mt-2">
          You're at {usage.percent}% of your monthly limit. Consider upgrading your plan to avoid interruption.
        </p>
      )}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-slate-gray-200">
      <div className="w-9 h-9 rounded-lg bg-cornflower-blue-50 flex items-center justify-center flex-shrink-0">
        <i className={`fa ${icon} text-cornflower-blue-500`} />
      </div>
      <div>
        <h3 className="text-base font-bold text-slate-gray-800">{title}</h3>
        <p className="text-xs text-slate-gray-500 mt-0.5 leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}
